import { CSSProperties, useMemo, useState } from "react";
import { ProgressBar, ToolTipString } from "../..";
import { colors, fonts } from "../../../Styles";
import { useTranslation } from "react-i18next";
import { penTypes } from "../../../Definitions/valueTypes";

import { useAppSelector } from "../../../Store";
import { Graph_QL } from "../../../Services";
import { LfLocation } from "../../../Services/Agrisys_GqlDataCaller/LocationFeedingCalls/GQLCalls/getLfStructureCall";

interface PenBasic_Props {
  pen?: LfLocation;
  penType: penTypes;
  hasHoverEffect?: boolean;
  hasSelectEffect?: boolean;
  onClick?: () => void;
  selectable?: boolean;
  borderRadius?: CSSProperties;
  contentCursor?: CSSProperties;
  selectFilter?: boolean;
  gridArea?: string
}
/**
 * PEN_BASIC
 * @param penId Unique identifier for each pen.
 * @param penType Basic, advanced and single. Each renders different content of animal data.
 * @param hasHoverEffect Boolean that, if true, adds a hover effect to the pen.
 * @param onClick Dynamic onClick event handler.
 * @returns a pen with varying content of animal data for Freeda Location Feeder systems.
 */
export default function PEN_BASIC({ pen, penType, hasHoverEffect, onClick, selectable = false, borderRadius, contentCursor, selectFilter, hasSelectEffect=false, gridArea}: PenBasic_Props) {
  const { t } = useTranslation();
  const [isHovered, setIsHovered] = useState(false);
  const [isSelected, setIsSelected] = useState(false);

  // RootStyle based on advanced content
  const _width =
    penType === penTypes.basic || penType === penTypes.penName ? "80px"
      : penType === penTypes.advanced ? "650px"
      : penType === penTypes.simple ? "650px"
      : "0px";

  const _height =
    penType === penTypes.basic || penType === penTypes.penName ? "30px"
      : penType === penTypes.advanced ? "35px"
      : penType === penTypes.simple ? "35px"
      : "0px";

  const _contentCursor = contentCursor ? contentCursor : { cursor: "default" };

  // On pen click
  const handleClick = () => {
    if (selectable && onClick) {
      setIsSelected((prev) => !prev);
      onClick();
    }
    if (!selectable && animal && onClick) {
      onClick();
    }
  };

  // Animals
  const animal = useMemo(() => {
    return pen?.animals ? pen.animals[0] : undefined;
  }, [pen?.animals]) 

  // General animal info
  const animalInfo = useMemo(() => {
    // Feed Correction
    const feedCorrection = animal
    ? animal.correction === 0 || animal.correction === undefined || animal.correction === null
      ? "0%"
      : animal.correction + "%"
    : "0%";
    
    // Milestone Day
    const milestoneDayNo = animal?.milestone_day
    ? Math.floor(
        (new Date().getTime() - animal.milestone_day) / (1000 * 60 * 60 * 24)
      )
    : t("noData", { defaultValue: "no data" });

    // Milestone Name
    const milestoneName = animal?.milestone ? animal.milestone.name : "";
    
    return {
      feedCorrection,
      milestoneDayNo,
      milestoneName
    };
  }, [animal, t])

  // ConsumptionProgressBar
  const ConsumptionProgressBar: JSX.Element = useMemo(() => {
    const progress = (animal?.consumptions?.[0])
    const progressBarTotal = (progress?.total ?? 0) / 1000;
    const progressBarProgressed = (progress?.progressed ?? 0) / 1000;

    return (
      <ProgressBar
        total={+(progressBarTotal).toFixed(2)}
        progressed={+(progressBarTotal - progressBarProgressed).toFixed(2)}
      />
    );
  }, [animal?.consumptions])

  // #region | Content
  const PenTypes = () => {
    switch (penType) {
      case penTypes.advanced:
        return (<>
          {animal ? (
            <div style={{ display: "grid", gridTemplateColumns: "22px 45px 45px 108px 45px 108px 45px 100px 45px 22px", textAlign: "start", alignItems: "center", gap: "5px" }}>
              <ToolTipString content="" overrides={{ contentCursor: { ..._contentCursor } }} />
              <ToolTipString tooltipString={t("locationFeederPages.locationFeeder.pen", { defaultValue: "Pen" })} content={pen?.name} overrides={{ contentCursor: { ..._contentCursor } }} />
              <ToolTipString tooltipString={t("params.animalId", { defaultValue: "Animal No." })} content={animal.animal_number} overrides={{ contentCursor: { ..._contentCursor } }} />
              <ToolTipString tooltipString={t("milestones.general.latestMilestone", { defaultValue: "Curr. milestone" })} content={animalInfo.milestoneName} overrides={{ contentCursor: { ..._contentCursor } }} />
              <ToolTipString tooltipString={t("milestones.general.milestoneDay", { defaultValue: "Curr. milestone day" })} content={animalInfo.milestoneDayNo} overrides={{ contentCursor: { ..._contentCursor } }} />
              <ToolTipString tooltipString={t("feed.feedCurve.feedCurve", { defaultValue: "Feed Curve" })} content={animal.feedCurve?.name} overrides={{ string: { paddingLeft: "5px" }, contentCursor: { ..._contentCursor } }} />
              <ToolTipString tooltipString={t("feed.feedCorrection.feedCorrection", { defaultValue: "Feed Corrections" })} content={animalInfo.feedCorrection} overrides={{ string: { paddingLeft: "5px" }, contentCursor: { ..._contentCursor } }} />
              <ToolTipString tooltipString={`${t("feed.general.remainingToday", { defaultValue: "Remaining Today" })} (${t("units.kg", { defaultValue: "KG" })})`} content={ConsumptionProgressBar} overrides={{ contentCursor: { ..._contentCursor } }} />
              <span></span>
              <span></span>
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "22px 45px 521px 22px", textAlign: "start", alignItems: "center", gap: "5px" }}>
              <span></span>
              <ToolTipString tooltipString={t("locationFeederPages.locationFeeder.pen", { defaultValue: "Pen" })} content={pen?.name} overrides={{ string: { cursor: "default" }, contentCursor: { ..._contentCursor } }} />
              <ToolTipString content={t("locationFeederPages.sections.emptyPen", { defaultValue: "Empty pen" })} overrides={{ string: { opacity: "0.5", cursor: "default" }, contentCursor: { ..._contentCursor } }} />
              <span></span>
            </div>
          )}
        </> );
      case penTypes.simple:
        return (<>
          {animal ? (
            <div style={{ display: "grid", gridTemplateColumns: "108px 45px 45px 417px", textAlign: "start", alignItems: "center", gap: "5px", cursor: "default" }}>
              <ToolTipString tooltipString={t("animalPages.animal.feedCurve", { defaultValue: "Feed Curve" })} content={animal.feedCurve?.name} overrides={{ string: { paddingLeft: "5px", cursor: "default" }, contentCursor: { ..._contentCursor } }} />
              <ToolTipString tooltipString={t("feed.feedCorrection.feedCorrection", { defaultValue: "Feed Correction" })} content={animalInfo.feedCorrection} overrides={{ string: { paddingLeft: "5px", cursor: "default" }, contentCursor: { ..._contentCursor } }} />
              <span></span>
              <span></span>
            </div>
          ) : (
            <></>
          )}
        </>);
      case penTypes.basic:
        return ( <>
          {animal ? (
            <span>{animal.animal_number}</span>
          ) : (
            <span style={{ opacity: "0.5" }}>{t("locationFeederPages.sections.emptyPen", {defaultValue: "Empty pen"})}</span>
          )}
        </> );
      case penTypes.penName:
        return (<>
          <span>{pen?.name}</span>
        </>)
      default:
        return null;
    }
  }
  // #endregion

  return (
    <div
      onClick={handleClick}
      onMouseEnter={
        hasHoverEffect && animal ? () => setIsHovered(true) : undefined
      }
      onMouseLeave={
        hasHoverEffect && animal ? () => setIsHovered(false) : undefined
      }
      style={{
        height: _height,
        width: _width,
        cursor: hasHoverEffect && animal ? "pointer" : "",
        boxSizing: "border-box",
        gridArea: gridArea ?? undefined
      }}
    >
      <div
        style={{
          width: "100%",
          height: "100%",
          textAlign: "center",
          alignContent: "center",
          boxSizing: "border-box",
          padding: "0px 10px",
          backgroundColor: isHovered ? colors.greyLightPrimary : colors.greyLightSecondary,
          filter: hasSelectEffect ? selectFilter? "opacity(1)" : "opacity(0.3)" : "opacity(1)",
          transition: "filter 0.3s",
          color: colors.greyDarkPrimary,
          ...fonts.paragraph,
          ...fonts.fontSize4,
          ...borderRadius,
        }}
      >
        {PenTypes()}
      </div>
    </div>
  );
}
